import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { format, parseISO, subDays, eachDayOfInterval } from "date-fns";
import { it as itLocale, enUS } from "date-fns/locale";
import { toast } from "sonner";
import { Calendar } from "@/components/ui/calendar";
import { seasonalTotal } from "../data/apartments";
import { useApartments } from "../hooks/useApartments";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Booking = ({ t, lang }) => {
  const APARTMENTS = useApartments();
  const [apartmentId, setApartmentId] = useState(APARTMENTS[0].id);
  const [range, setRange] = useState({ from: undefined, to: undefined });
  const [booked, setBooked] = useState([]);
  const [form, setForm] = useState({ name: "", email: "", phone: "", guests: 2, message: "", consent: false });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(null);

  useEffect(() => {
    const preset = sessionStorage.getItem("vn_apartment");
    if (preset && APARTMENTS.some((a) => a.id === preset)) {
      setApartmentId(preset);
      sessionStorage.removeItem("vn_apartment");
    }
  }, [APARTMENTS]);

  useEffect(() => {
    setRange({ from: undefined, to: undefined });
    axios
      .get(`${API}/availability/${apartmentId}`)
      .then((res) => setBooked(res.data))
      .catch(() => setBooked([]));
  }, [apartmentId]);

  const apt = APARTMENTS.find((a) => a.id === apartmentId);
  const today = new Date();

  const disabled = useMemo(
    () => [
      { before: today },
      ...booked.map((b) => ({ from: parseISO(b.check_in), to: subDays(parseISO(b.check_out), 1) })),
    ],
    [booked]
  );

  const bookedSet = useMemo(() => {
    const s = new Set();
    booked.forEach((b) => {
      eachDayOfInterval({ start: parseISO(b.check_in), end: subDays(parseISO(b.check_out), 1) }).forEach((d) =>
        s.add(format(d, "yyyy-MM-dd"))
      );
    });
    return s;
  }, [booked]);

  const rangeConflicts = useMemo(() => {
    if (!range.from || !range.to) return false;
    return eachDayOfInterval({ start: range.from, end: subDays(range.to, 1) }).some((d) =>
      bookedSet.has(format(d, "yyyy-MM-dd"))
    );
  }, [range, bookedSet]);

  const nights = range.from && range.to ? Math.round((range.to - range.from) / 86400000) : 0;
  const base = nights > 0 ? seasonalTotal(apt.pricing, range.from, range.to) : 0;
  const discountPct = nights > 7 ? 10 : nights > 3 ? 5 : 0;
  const total = Math.round(base * (1 - discountPct / 100));

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!range.from || !range.to || rangeConflicts) {
      toast.error(t.booking.errorOverlap);
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/bookings`, {
        apartment_id: apartmentId,
        name: form.name,
        email: form.email,
        phone: form.phone || null,
        guests: Number(form.guests),
        check_in: format(range.from, "yyyy-MM-dd"),
        check_out: format(range.to, "yyyy-MM-dd"),
        message: form.message || null,
        privacy_consent: form.consent,
      });
      toast.success(t.booking.success);
      setSent({
        apartmentName: apt.name,
        nights,
        total,
        causale: `${t.booking.causalePrefix} ${form.name} – ${apt.name} · ${format(range.from, "dd/MM/yyyy")} – ${format(range.to, "dd/MM/yyyy")}`,
      });
      setForm({ name: "", email: "", phone: "", guests: 2, message: "", consent: false });
      setRange({ from: undefined, to: undefined });
      const res = await axios.get(`${API}/availability/${apartmentId}`);
      setBooked(res.data);
    } catch (err) {
      toast.error(err?.response?.status === 409 ? t.booking.errorOverlap : t.booking.errorGeneric);
    } finally {
      setSending(false);
    }
  };

  const inputCls =
    "w-full bg-transparent border-b border-[#C29B62]/40 focus:border-[#1C3829] outline-none py-3 text-base font-light placeholder:text-[#1A1A1A]/35 transition-colors";
  const labelCls = "block text-xs uppercase tracking-[0.2em] text-[#1A1A1A]/50 mb-1";

  return (
    <section id="prenota" data-testid="booking-section" className="py-24 lg:py-32">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="max-w-2xl">
          <p className="text-xs uppercase tracking-[0.35em] text-[#C29B62]">{t.booking.overline}</p>
          <h2 className="mt-4 text-4xl sm:text-5xl font-light tracking-tight text-[#1C3829]">{t.booking.title}</h2>
          <p className="mt-6 text-base text-[#4A4A4A] font-light leading-relaxed">{t.booking.sub}</p>
          <p className="mt-6 inline-block border border-[#C29B62]/40 bg-[#C29B62]/10 px-5 py-3 text-sm text-[#8a6a3a]" data-testid="booking-offers">
            {t.booking.offers}
          </p>
        </div>

        <div className="mt-14 grid grid-cols-1 lg:grid-cols-2 gap-14 lg:gap-20">
          <div>
            <p className={labelCls}>{t.booking.choose}</p>
            <div className="flex flex-wrap gap-3 mt-2" data-testid="apartment-selector">
              {APARTMENTS.map((a) => (
                <button
                  key={a.id}
                  type="button"
                  data-testid={`booking-select-${a.id}`}
                  onClick={() => setApartmentId(a.id)}
                  className={`px-5 py-3 text-sm border transition-colors duration-300 ${
                    apartmentId === a.id
                      ? "bg-[#1C3829] text-[#F7F5F0] border-[#1C3829]"
                      : "border-[#C29B62]/40 text-[#1A1A1A]/70 hover:border-[#1C3829] hover:text-[#1C3829]"
                  }`}
                >
                  {a.name}
                </button>
              ))}
            </div>

            <p className={`${labelCls} mt-10`}>{t.booking.dates}</p>
            <div className="mt-2 inline-block border border-[#C29B62]/25 bg-[#F7F5F0] p-2" data-testid="booking-calendar">
              <Calendar
                mode="range"
                selected={range}
                onSelect={(r) => setRange(r || { from: undefined, to: undefined })}
                disabled={disabled}
                numberOfMonths={2}
                locale={lang === "it" ? itLocale : enUS}
                className="rdp"
              />
            </div>
            <p className="mt-3 text-xs text-[#1A1A1A]/50">{t.booking.legend}</p>

            <div className="mt-6 text-base text-[#1A1A1A]" data-testid="booking-summary">
              {nights > 0 ? (
                <div>
                  <p>
                    <span className="font-serif-display text-2xl text-[#1C3829]">{apt.name}</span>
                    <span className="mx-3 text-[#C29B62]">·</span>
                    {nights} {t.booking.nights}
                    <span className="mx-3 text-[#C29B62]">·</span>
                    {t.booking.total}: <strong className="text-[#1C3829]" data-testid="booking-total">€{total}</strong>
                  </p>
                  {discountPct > 0 && (
                    <p className="mt-2 text-sm text-[#8a6a3a]" data-testid="booking-discount">
                      {t.booking.discount} −{discountPct}%: <s className="text-[#1A1A1A]/45">€{base}</s> (−€{base - total})
                    </p>
                  )}
                </div>
              ) : (
                <p className="text-[#1A1A1A]/50 font-light">{t.booking.selectDates}</p>
              )}
              {rangeConflicts && <p className="mt-2 text-sm text-red-700" data-testid="booking-conflict-warning">{t.booking.errorOverlap}</p>}
            </div>
          </div>

          <form onSubmit={submit} className="space-y-8" data-testid="booking-form">
            <div>
              <label className={labelCls} htmlFor="bk-name">{t.booking.name}</label>
              <input id="bk-name" data-testid="booking-name-input" required className={inputCls} value={form.name} onChange={set("name")} />
            </div>
            <div>
              <label className={labelCls} htmlFor="bk-email">{t.booking.email}</label>
              <input id="bk-email" data-testid="booking-email-input" required type="email" className={inputCls} value={form.email} onChange={set("email")} />
            </div>
            <div className="grid grid-cols-2 gap-8">
              <div>
                <label className={labelCls} htmlFor="bk-phone">{t.booking.phone}</label>
                <input id="bk-phone" data-testid="booking-phone-input" className={inputCls} value={form.phone} onChange={set("phone")} />
              </div>
              <div>
                <label className={labelCls} htmlFor="bk-guests">{t.booking.guests}</label>
                <input
                  id="bk-guests"
                  data-testid="booking-guests-input"
                  type="number"
                  min={1}
                  max={apt.guests}
                  required
                  className={inputCls}
                  value={form.guests}
                  onChange={set("guests")}
                />
              </div>
            </div>
            <div>
              <label className={labelCls} htmlFor="bk-message">{t.booking.message}</label>
              <textarea id="bk-message" data-testid="booking-message-input" rows={4} className={`${inputCls} resize-none`} value={form.message} onChange={set("message")} />
            </div>
            <div className="flex items-start gap-3">
              <input
                id="bk-consent"
                data-testid="booking-consent-checkbox"
                type="checkbox"
                required
                checked={form.consent}
                onChange={(e) => setForm({ ...form, consent: e.target.checked })}
                className="mt-1 w-4 h-4 accent-[#1C3829] shrink-0"
              />
              <label htmlFor="bk-consent" className="text-sm text-[#4A4A4A] font-light leading-relaxed">
                {t.booking.consentPre}{" "}
                <a href="/privacy" target="_blank" rel="noopener noreferrer" data-testid="booking-privacy-link" className="text-[#1C3829] underline underline-offset-4 hover:text-[#C29B62] transition-colors">
                  Privacy Policy
                </a>{" "}
                {t.booking.consentPost}
              </label>
            </div>
            <button
              data-testid="booking-submit-button"
              type="submit"
              disabled={sending || nights === 0 || rangeConflicts}
              className="bg-[#1C3829] text-[#F7F5F0] text-xs uppercase tracking-[0.25em] px-10 py-4 hover:text-[#C29B62] hover:-translate-y-0.5 transition-all duration-300 disabled:opacity-40 disabled:hover:translate-y-0 disabled:hover:text-[#F7F5F0]"
            >
              {sending ? t.booking.sending : t.booking.submit}
            </button>
          </form>
        </div>

        {sent && (
          <div data-testid="booking-success-panel" className="mt-12 border border-[#C29B62]/40 bg-white/70 p-8 max-w-2xl">
            <p className="text-xs uppercase tracking-[0.3em] text-[#C29B62]">{t.booking.paymentTitle}</p>
            <p className="mt-3 font-serif-display text-2xl text-[#1C3829]">
              {sent.apartmentName} · {sent.nights} {sent.nights === 1 ? t.booking.night : t.booking.nights}
            </p>
            <p className="mt-4 text-base text-[#4A4A4A] font-light leading-relaxed">{t.booking.paymentText}</p>
            <dl className="mt-6 space-y-3">
              <div className="flex flex-wrap gap-x-3">
                <dt className="w-40 pt-0.5 text-xs uppercase tracking-[0.2em] text-[#1A1A1A]/50">{t.booking.beneficiary}</dt>
                <dd className="text-base text-[#1A1A1A]">Villa Navagero SRL</dd>
              </div>
              <div className="flex flex-wrap gap-x-3">
                <dt className="w-40 pt-0.5 text-xs uppercase tracking-[0.2em] text-[#1A1A1A]/50">IBAN</dt>
                <dd className="font-mono text-base font-medium text-[#1C3829]" data-testid="booking-iban">IT46 N053 8702 0000 0004 7639 146</dd>
              </div>
              <div className="flex flex-wrap gap-x-3">
                <dt className="w-40 pt-0.5 text-xs uppercase tracking-[0.2em] text-[#1A1A1A]/50">{t.booking.amount}</dt>
                <dd className="text-base text-[#1A1A1A]" data-testid="booking-amount">€{sent.total}</dd>
              </div>
              <div className="flex flex-wrap gap-x-3">
                <dt className="w-40 pt-0.5 text-xs uppercase tracking-[0.2em] text-[#1A1A1A]/50">{t.booking.causale}</dt>
                <dd className="text-base text-[#1A1A1A]" data-testid="booking-causale">{sent.causale}</dd>
              </div>
            </dl>
            <p className="mt-6 text-sm text-[#4A4A4A] font-light italic">{t.booking.paymentNote}</p>
            <button
              type="button"
              data-testid="booking-new-request"
              onClick={() => setSent(null)}
              className="mt-6 text-xs uppercase tracking-[0.2em] text-[#1C3829] border-b border-[#C29B62] pb-1 hover:text-[#C29B62] transition-colors"
            >
              {t.booking.newRequest}
            </button>
          </div>
        )}
      </div>
    </section>
  );
};
